/**
 * Opens a 1-on-1 chat with another member from anywhere in the app.
 *
 * Mirrors the mobile chat screen: the shared context reuses the existing
 * DirectChat thread when the inbox already has one and only calls
 * Conversation/Initiate otherwise (recovering from a 409 by refetching), so the
 * caller never has to know whether the conversation exists yet.
 */
import { useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import messageContext from '@wlb/shared/context/MessageContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import type { OpenDirectConversationInput } from '@wlb/shared/types/context';

export interface UseOpenDirectConversationResult {
  openConversation: (input: OpenDirectConversationInput) => Promise<void>;
  /** Peer id currently being resolved, so lists can show per-row progress. */
  openingUserId: string | null;
}

export function useOpenDirectConversation(): UseOpenDirectConversationResult {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Read optionally: the public feed lives outside MessageProvider, which only
  // mounts for a signed-in visitor. Callers there gate the action behind the
  // auth prompt, so a missing provider simply means there is nothing to open.
  const messages = useContext(messageContext);
  const [openingUserId, setOpeningUserId] = useState<string | null>(null);

  const openConversation = useCallback(
    async (input: OpenDirectConversationInput) => {
      if (!messages) return;

      const peerId = Number(input.userId);
      if (!Number.isFinite(peerId) || peerId <= 0) {
        ToastService.show({ type: 'error', message: t('chat.errors.invalidUserId') });
        return;
      }

      const peerIdStr = String(peerId);
      setOpeningUserId(peerIdStr);
      try {
        const conversation = await messages.openOrCreateDirectConversation({
          userId: peerId,
          name: input.name ?? null,
          avatar: input.avatar || null,
        });
        navigate(`/messages/${conversation.id}`);
      } catch {
        ToastService.show({ type: 'error', message: t('chat.errors.openFailed') });
      } finally {
        setOpeningUserId(prev => (prev === peerIdStr ? null : prev));
      }
    },
    [messages, navigate, t],
  );

  return { openConversation, openingUserId };
}
